import {
  MarketingPageElementLocation,
  MarketingPageElementType
} from '@lucid/gatsby-theme-lucid';
import { graphql } from 'gatsby';
import { Box, Heading } from 'lucid-components-react';
import React from 'react';
import LazyLoad from 'react-lazyload';

import Link from 'components/link/link';
import LucidContainer from 'components/lucid-container/lucid-container';
import { Drupal_ParagraphNineteenQuote } from 'generated/graphql';
import { Text } from 'theme/typography';
import { LucidTheme } from 'types/types';

interface Props {
  entity: Drupal_ParagraphNineteenQuote;
}

const NineteenQuote = ({
  entity: { fieldQuote, fieldImage, fieldAttribution, fieldQuoteLink }
}: Props) => {
  const linkPath = fieldQuoteLink?.url?.path;
  const finalPath = linkPath?.includes('node')
    ? fieldQuoteLink?.url?.translate?.path
    : linkPath;

  return (
    <Box
      as="section"
      className="nineteen-quote"
      pb={8}
      pt={{ default: 6, mobile: 8 }}
    >
      <LucidContainer
        flex
        css={(theme: LucidTheme) => ({
          justifyContent: 'center',
          flexWrap: 'wrap',
          alignItems: 'center',
          [`@media screen and (min-width: ${theme.breakpoints.mobile})`]: {
            justifyContent: 'flex-end'
          },
          [`@media screen and (min-width: ${theme.breakpoints.tabletSmall})`]: {
            justifyContent: 'space-between',
            flexWrap: 'nowrap'
          }
        })}
      >
        <Box
          css={(theme: LucidTheme) => ({
            flex: '0 1 100%',
            order: 2,
            [`@media screen and (min-width: ${theme.breakpoints.tabletSmall})`]: {
              flex: '0 1 75%',
              order: 'unset'
            },
            [`@media screen and (min-width: ${theme.breakpoints.tablet})`]: {
              flex: '0 1 65%'
            }
          })}
        >
          <Text
            css={(theme: LucidTheme) => ({
              marginTop: theme.space[0]
            })}
            textStyles="quoteM"
          >
            {fieldQuote}
          </Text>
          <Heading variant="subheadXXS">{`—${fieldAttribution}`}</Heading>
        </Box>
        <Box
          css={(theme: LucidTheme) => ({
            order: 1,
            marginBottom: theme.space[5],
            padding: theme.space[0],
            minWidth: '100%',
            maxWidth: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            [`@media screen and (min-width: ${theme.breakpoints.mobile})`]: {
              alignItems: 'flex-end'
            },
            [`@media screen and (min-width: ${theme.breakpoints.tabletSmall})`]: {
              padding: `${theme.space[0]} ${theme.space[0]} ${theme.space[0]} ${theme.space[5]}px`,
              flex: '0 1 19%',
              maxWidth: '210px',
              minWidth: '210px',
              alignItems: 'flex-start'
            }
          })}
        >
          <LazyLoad>
            <img
              alt={fieldAttribution!}
              css={(theme: LucidTheme) => ({
                borderRadius: '50%',
                maxWidth: '150px',
                maxHeight: '150px',
                [`@media screen and (min-width: ${theme.breakpoints.tabletSmall})`]: {
                  maxWidth: '178px',
                  minHeight: '178px'
                }
              })}
              src={fieldImage!}
            />
          </LazyLoad>
          {finalPath && (
            <Link
              primary
              beaconEvent={{
                name: 'clickedMarketingPageElement',
                elementLocation: MarketingPageElementLocation.body,
                elementDetail: 'case-study',
                elementType: MarketingPageElementType.link
              }}
              css={(theme: LucidTheme) => ({
                marginTop: theme.space[2]
              })}
              to={finalPath}
            >
              {fieldQuoteLink?.title}
            </Link>
          )}
        </Box>
      </LucidContainer>
    </Box>
  );
};

export default NineteenQuote;

export const query = graphql`
  fragment nineteenQuote on Drupal_ParagraphNineteenQuote {
    fieldAttribution
    fieldImage
    fieldQuote
    fieldQuoteLink {
      title
      url {
        path
        translate(language: EN) {
          path
        }
      }
    }
  }
`;
